// src/hooks/useEntitlement.js
//
// ============================================================================
// IS THIS COMPANY ALLOWED TO MAKE CHANGES RIGHT NOW?
// ============================================================================
//
// The frontend counterpart to server/services/entitlementService.js.
//
// SEPARATE FROM useFeatures, ON PURPOSE
//   useFeatures answers "which product did they buy" — canPrice, canDrawings,
//   from the subscription tier. That is a capability and it changes only when
//   the plan changes.
//
//   This answers "are they still paying for it". A lapsed Drawing customer is
//   still a Drawing customer; they simply cannot save. Folding the two
//   together would mean a component could not tell "never had drawings" from
//   "used to have drawings", which are very different things to show someone.
//
// THIS IS NOT THE ENFORCEMENT
//   The server refuses writes with 402 regardless of what this returns
//   (server/middleware/requireEntitlement.js). This hook exists so the app can
//   SAY SO IN ADVANCE — nobody should spend twenty minutes on a drawing and
//   discover at save time that their account lapsed a week ago.
//
//   Because enforcement lives on the server, a component that forgets to check
//   this produces a confusing error message, not a security hole. That is the
//   whole reason the frontend work here is small.
//
// FAILS OPEN — THE OPPOSITE OF useFeatures
//   useFeatures defaults every flag to false while loading, so gated features
//   stay hidden rather than flashing visible-then-gone.
//
//   This defaults canWrite to TRUE. A slow or failed entitlement fetch must
//   not make a paying customer's app go read-only. The cost of being wrong in
//   this direction is one refused save with a clear message; the cost in the
//   other direction is a shop unable to work because a request was slow.
//
// USAGE
//   const { isLocked, isGrace, message, resubscribeUrl } = useEntitlement();
//
//   {isLocked && <LockedBanner />}
//   <button disabled={isLocked}>Save</button>

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { useCompany } from './useCompany.js';
import { useAuth } from './useAuth.js';

const OPEN = {
  state: 'active',
  canWrite: true,
  message: null,
  periodEnd: null,
  graceEndsAt: null,
  reason: null,
};

const useEntitlement = () => {
  const { selectedCompany } = useCompany();
  const { isAuthenticated } = useAuth();
  const companyId = selectedCompany?.companyId ?? null;

  const [entitlement, setEntitlement] = useState(OPEN);
  const [isLoading, setIsLoading] = useState(true);

  // Only ask when there is someone to ask about.
  //
  // THIS GUARD IS LOAD-BEARING. This hook is mounted globally through
  // LockedBanner in App.js, so it runs on the marketing site, /pricing,
  // /register and the login screen too. Calling an authenticated endpoint
  // from there returns 401, and api.js answers a failed 401 refresh with
  // `window.location.href = '/'` — which remounts this hook, which fires
  // again. That is an infinite reload loop, and it presents as webpack
  // chunk-load failures because the page never survives long enough to
  // finish fetching one.
  const shouldFetch = Boolean(isAuthenticated && companyId);

  const fetchEntitlement = useCallback(async () => {
    if (!shouldFetch) {
      // Logged out, or no company selected yet. Nothing is gated for them —
      // and every screen they can reach is public.
      setEntitlement(OPEN);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get('/subscriptions/entitlement', {
        params: { companyId },
      });
      if (response.data?.entitlement) {
        setEntitlement(response.data.entitlement);
      }
    } catch (err) {
      // Fail open. A hook that cannot reach the server must not conclude the
      // account is locked — see the note above.
      console.error('Could not resolve entitlement:', err);
      setEntitlement(OPEN);
    } finally {
      setIsLoading(false);
    }
  }, [shouldFetch, companyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchEntitlement();
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [fetchEntitlement]);

  // A refused write is authoritative — more current than whatever was fetched
  // on page load, since a subscription can lapse mid-session. api.js dispatches
  // this from its 402 handler so the banner appears on the first rejection
  // without any component having to report upward.
  useEffect(() => {
    const onLocked = (event) => {
      if (!shouldFetch) return; // not signed in — nothing to warn about

      // Flip to locked, but KEEP prev.message.
      //
      // The event carries the REFUSAL wording ("changes can't be saved"),
      // which belongs to the one action that was just blocked and is displayed
      // by that component's own error box. `message` here is the standing
      // read-only wording the banner shows continuously. Overwriting it would
      // leave a present-tense refusal parked at the top of the screen long
      // after the click that caused it.
      setEntitlement(prev => ({
        ...prev,
        state: event.detail?.entitlement || 'locked',
        canWrite: false,
      }));
    };

    window.addEventListener('subscriptionLocked', onLocked);
    return () => window.removeEventListener('subscriptionLocked', onLocked);
  }, [shouldFetch]);

  // The subscription changed somewhere else in the app. Go and ask again.
  //
  // WHY AN EVENT AND NOT A PROP OR A CONTEXT
  //   LockedBanner mounts once, globally, in App.js. Account.js has its own
  //   useEntitlement() call with its own state, so calling refresh() there
  //   updates Account and leaves the banner untouched. There is no parent to
  //   lift the state into short of wrapping the app in a provider, which is a
  //   large change for one strip of colour.
  //
  //   The window event is how subscriptionLocked above already crosses that
  //   same gap, from api.js. This is the same channel used in the same way.
  //
  // WHAT IT FIXES, IN BOTH DIRECTIONS
  //   Resubscribing left a "your subscription is cancelled" banner sitting
  //   above a plan card that correctly read Active — the card refreshes, the
  //   banner never heard. Cancelling has the mirror problem: the banner that
  //   should appear does not, until the next full page load.
  //
  // TIMING MATTERS. Dispatch this only once the change has actually landed in
  // our own database, not when the customer returns from Stripe. company_
  // subscriptions is written by the webhook, so firing on arrival refetches
  // the OLD state and pins the stale banner in place — see the
  // awaitingActivation poll in Account.js, which exists for this reason.
  //
  // Unlike onLocked, this refetches rather than setting state directly: the
  // server owns the copy, and a resubscribe needs the message cleared, not
  // rewritten.
  useEffect(() => {
    const onChanged = () => {
      if (!shouldFetch) return;
      fetchEntitlement();
    };

    window.addEventListener('subscriptionChanged', onChanged);
    return () => window.removeEventListener('subscriptionChanged', onChanged);
  }, [shouldFetch, fetchEntitlement]);

  return {
    state: entitlement.state,
    canWrite: entitlement.canWrite !== false,

    // Named states, so components read as intent rather than string comparison.
    isLocked: entitlement.state === 'locked',
    isGrace: entitlement.state === 'grace',
    isActive: entitlement.state === 'active',
    isFree: entitlement.state === 'free',

    // Copy originates on the server so the banner and any 402 body cannot
    // describe the same situation differently.
    message: entitlement.message,
    periodEnd: entitlement.periodEnd,
    graceEndsAt: entitlement.graceEndsAt,

    // Which KIND of grace this is. Both 'grace_window' and
    // 'paid_period_remaining' arrive as state 'grace', but they want different
    // dates: the first is a real seven-day deadline (graceEndsAt), the second
    // is a cancelled subscription with paid time still on it, where the date
    // that matters is periodEnd and may be months away.
    //
    // Passed through rather than inferred from periodEnd > now, because the
    // server already made this determination and two places deciding it
    // separately is how they drift apart.
    reason: entitlement.reason ?? null,
    resubscribeUrl: '/pricing',

    isLoading,

    // Call after a successful resubscribe so the banner clears without a
    // page reload.
    refresh: fetchEntitlement,
  };
};

export default useEntitlement;
export { useEntitlement };